import { useQuery } from "@tanstack/react-query";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { TransitionGroup } from "react-transition-group";

// Helpers
import { getMappedOEMFranchises } from "@/Common/helpers/OEMs";
import { and } from "@/Common/helpers/sonar";
import { getSections, getSectionsAvailable } from "@PayPlans/helpers/sections";

// Defaults builders
import {
	AutoAlertsDefault,
	BuildVariableOperationsGpsBonusSetup,
	CallActivityPerformanceDefault,
	CustomerSatisfactionDefault,
	DemoAllowance,
	LongevityDefault,
	ModelBased,
	PacksDefault,
	TopPerformanceBonusDefault,
	VolumeBonusDefault,
} from "@PayPlans/helpers/DefaultSections";

// API's
import { getSalesPerformanceFormatVersionProducts } from "@Common/api/salesPerformance/queries";
import { getMakes } from "@Common/api/vehicles";

// Contexts/Hooks
import { useLocationFS } from "@/Common/hooks/LocationFS";
import { getExtendedBranchesFranchises, getOEMFranchisePrograms } from "@/Common/hooks/LocationPrograms";
import { usePayPlan } from "@PayPlans/context/PayPlans";

// Layouts
import PayPlansLayout from "@/PayPlans/Layouts/PayPlansLayout";
import SectionContainer from "@Layouts/components/SectionContainer";

// Components
import PdfPayPlanLayer from "@/PayPlans/views/Layers/Html/Sales/Associates";

// Custom Sections
import {
	AutoAlerts,
	BonusAdjustments,
	CallActivityPerformance,
	CustomerSatisfaction,
	DefaultCommissions,
	Longevity,
	MiniDeals,
	ModelCommissions,
	Packs,
	TopSalesBonus,
	VariableOperationsGpsBonus,
	VolumeBonus,
} from "./sections";

// MUI
import { Collapse, Grid } from "@mui/material";

const allSections = getSections(
	[
		"modelBased",
		"volumeBonus",
		"variableOperationsGpsBonus",
		"demoAllowance",
		"packs",
		"customerSatisfaction",
		"autoAlerts",
		"topSalesBonus",
		"callActivityPerformance",
		"longevity",
	],
	[{ section: "topSalesBonus", modify: { title: "Top Sales Associate Bonus", description: "Note for top sales associate bonus" } }]
);

// Used to build franchises dependant sections
const excludesMakes = ["Cadillac"];

// Constants
const contentAlignment = "flex-start";

const OPTIONAL_SECTIONS = new Map([
	["callActivityPerformance", CallActivityPerformanceDefault],
	["modelBased", ModelBased],
	["volumeBonus", VolumeBonusDefault],
	["variableOperationsGpsBonus", BuildVariableOperationsGpsBonusSetup],
	["demoAllowance", DemoAllowance],
	["packs", PacksDefault],
	["customerSatisfaction", CustomerSatisfactionDefault],
	["topSalesBonus", TopPerformanceBonusDefault],
	["autoAlerts", AutoAlertsDefault],
	["longevity", LongevityDefault],
]);

const getNewBrands = (franchises = []) => franchises.map((franchise) => ({ _id: franchise, make: franchise }));
const getIsSectionButtonDisabled = ({ isValid = false, isReadOnlyReason = false, isSomeNewPayPlan = false, loading = false }) =>
	!isValid || isReadOnlyReason || isSomeNewPayPlan || loading;

export default function PayPlansModel(props) {
	const { data = null, location = null, isNew = false, loading = false, readOnly = false, saving = false, updateModel = () => {} } = props;

	const { isSomeNewPayPlan, isValid, selected, useSectionsErrors, position } = usePayPlan();
	const { errors, clearErrors, clearSectionErrors } = useSectionsErrors();
	const { data: locationFS, isLoading: loadingFS } = useLocationFS({ locationId: location.locationId });

	const isReadOnly = !loading && Boolean(data?.available);

	// States
	const [isLazyLoading, setIsLazyLoading] = useState(true);
	const [sections, setSections] = useState(new Map());

	const { data: makesList, isLoading: loadingMakes } = useQuery({
		queryKey: ["makes"],
		queryFn: async () => getMakes(),
		staleTime: Infinity,
		enabled: !!selected,
	});

	const { data: formatVersion, isLoading: loadingProducts } = useQuery({
		queryKey: ["salesPerformanceProducts", location?.locationId, data?.formatVersion],
		queryFn: async () => getSalesPerformanceFormatVersionProducts(location.locationId, data.formatVersion),
		staleTime: Infinity,
		enabled: !!location?.locationId && !!data?.formatVersion,
	});

	// Programs usage
	const programsUsage = useMemo(() => {
		const extendedBranchesFranchises = getExtendedBranchesFranchises(location?.branchesOEMs, location?.branchesFranchises);
		return getOEMFranchisePrograms(locationFS?.oems, extendedBranchesFranchises);
	}, [location, locationFS]);

	// Sections to disable
	const disableSections = programsUsage?.filter((item) => item.disabled).map((item) => item.program) || [];

	useMemo(() => {
		setSections(getSectionsAvailable(allSections, data));
	}, [data]);

	// Sections controls
	const handleAddSection = (key) => {
		const sectionFn = OPTIONAL_SECTIONS.get(key);
		const isRequiredSectionParams = ["volumeBonus", "customerSatisfaction", "variableOperationsGpsBonus"].includes(key);

		if (isRequiredSectionParams) {
			switch (key) {
				case "variableOperationsGpsBonus":
					data[key] = sectionFn({ franchises: location?.franchises });
					break;
				case "volumeBonus":
					data[key] = sectionFn(location?.franchises || []);
					break;
				case "customerSatisfaction":
					const mappedOEMFranchises = getMappedOEMFranchises(location?.branchesOEMs ?? [], location?.branchesFranchises ?? []);
					data[key] = sectionFn(mappedOEMFranchises ?? [], excludesMakes);
					break;
			}
		} else {
			data[key] = sectionFn();
		}

		setSections(getSectionsAvailable(allSections, data));
	};

	const handleDeleteSection = (section) => {
		const remove = allSections.get(section);

		// Remove section
		data[section] = null;
		if (remove?.type === "list") {
			data[section] = [];
		}

		clearSectionErrors(section);
		setSections(getSectionsAvailable(allSections, data));
		updateModel({ section, data: data[section] });
	};

	const isACommonUpdateSection = (section) => {
		const commons = [
			"autoAlerts",
			"demoAllowance",
			"longevity",
			"topSalesBonus",
			"volumeBonus",
			"callActivityPerformance",
			"variableOperationsGpsBonus",
		];
		return commons.includes(section);
	};

	const isBasedOnUpdateSection = (section) => {
		const basedOn = ["miniDeal", "packs"];
		return basedOn.includes(section);
	};

	const handleUpdateSection = (section, update = "", value = null, isValidUpdate = true) => {
		const isCommonUpdatable = isACommonUpdateSection(section);
		const isBasedOnUpdate = isBasedOnUpdateSection(section);

		if (isCommonUpdatable) {
			data[section] = value;
		} else if (isBasedOnUpdate) {
			data[section][update] = value;
		} else {
			switch (section) {
				case "default":
				case "modelBased":
					if (update === "new" || update === "preOwned") {
						data[section]["content"][update] = value;
					} else {
						data[section] = { ...data[section], ...value };
					}
					break;
				case "customerSatisfaction":
					if (update === "weighting") {
						data[section][update] = value;
						break;
					}

					data[section]["makeMetrics"]?.forEach((makeMetric, key) => {
						if (makeMetric.make === update) {
							data[section]["makeMetrics"][key] = value;
						}
					});

					break;
				default:
					break;
			}
		}

		// Update model
		Boolean(isValidUpdate) && updateModel({ section, data: data[section] });
	};

	const isDOMLoading = useMemo(() => Boolean(isLazyLoading || loading || loadingFS), [isLazyLoading, loading, loadingFS]);

	useMemo(() => {
		const timer = 500;
		setIsLazyLoading(true);
		if (selected) {
			setTimeout(() => {
				setIsLazyLoading(false);
			}, timer);
		}
	}, [selected, setIsLazyLoading]);

	useEffect(() => {
		return () => {
			clearErrors();
		};
	}, [clearErrors]);

	const isReadOnlyReason = readOnly || isReadOnly;

	return (
		<PayPlansLayout
			htmlComponent={<PdfPayPlanLayer payPlan={data} selected={selected} loading={loading} />}
			data={data}
			allSections={allSections}
			sections={sections}
			disabledSections={disableSections}
			disabled={getIsSectionButtonDisabled({
				isValid,
				isReadOnlyReason,
				isSomeNewPayPlan,
				loading,
			})}
			isNew={isNew}
			isValid={isValid}
			loading={loading}
			readOnly={isReadOnlyReason}
			onActionAddSection={handleAddSection}
		>
			<TransitionGroup component={Grid} container item xs={12}>
				<Collapse sx={{ width: 1 }}>
					<SectionContainer
						sectionId={"default"}
						title={"Default Commissions"}
						collapsible
						loading={loading}
						readOnly={readOnly || isReadOnly}
					>
						<DefaultCommissions
							sectionId={"default"}
							applyTiersRetroactively={Boolean(data?.default?.applyTiersRetroactively)}
							content={data?.default?.content}
							errors={errors?.get("default")}
							formatVersion={formatVersion}
							globalUnitCounts={Boolean(data?.default?.globalUnitCounts)}
							newAndPreOwnedRequired={Boolean(data?.default?.newAndPreOwnedRequired)}
							isGuaranteedDraw={Boolean(data?.default?.isGuaranteedDraw)}
							loading={loading}
							loadingProducts={loadingProducts}
							readOnly={readOnly || isReadOnly}
							onActionUpdate={handleUpdateSection}
						/>
					</SectionContainer>
				</Collapse>

				<Collapse sx={{ width: 1, marginTop: 2 }}>
					<SectionContainer
						sectionId={"miniDeal"}
						title={"Mini-deal and Cap"}
						collapsible
						loading={loading}
						readOnly={readOnly || isReadOnly}
					>
						<MiniDeals
							sectionId={"miniDeal"}
							brands={{
								new: getNewBrands(location?.franchises || []),
								preOwned: makesList,
							}}
							miniDeal={data?.miniDeal}
							errors={errors?.get("miniDeal")}
							loading={loading}
							loadingMakes={loadingMakes}
							readOnly={readOnly || isReadOnly}
							onActionUpdate={handleUpdateSection}
						/>
					</SectionContainer>
				</Collapse>

				{sections.get("modelBased") && (
					<Collapse sx={{ width: 1, marginTop: 2 }}>
						<SectionContainer
							sectionId={"modelBased"}
							title={sections.get("modelBased").title}
							collapsible
							deletable
							loading={loading}
							readOnly={readOnly || isReadOnly}
							onDeleteAction={handleDeleteSection}
						>
							<ModelCommissions
								sectionId={"modelBased"}
								applyTiersRetroactively={data?.modelBased?.applyTiersRetroactively}
								content={data?.modelBased?.content}
								errors={errors?.get("modelBased")}
								formatVersion={formatVersion}
								globalUnitCounts={data?.modelBased?.globalUnitCounts}
								makes={makesList}
								loading={loading}
								loadingProducts={loadingProducts}
								loadingMakes={loadingMakes}
								readOnly={readOnly || isReadOnly}
								onActionUpdate={handleUpdateSection}
							/>
						</SectionContainer>
					</Collapse>
				)}

				{and(Boolean(sections.get("callActivityPerformance")), !isDOMLoading) && (
					<Collapse sx={{ width: 1, marginTop: 2 }}>
						<SectionContainer
							sectionId={"callActivityPerformance"}
							title={sections.get("callActivityPerformance").title}
							collapsible
							deletable
							loading={loading}
							readOnly={readOnly || isReadOnly}
							onDeleteAction={handleDeleteSection}
						>
							<CallActivityPerformance
								sectionId={"callActivityPerformance"}
								performance={data?.callActivityPerformance || null}
								useSectionsErrors={useSectionsErrors}
								loading={loading}
								readOnly={readOnly || isReadOnly}
								onActionUpdate={handleUpdateSection}
							/>
						</SectionContainer>
					</Collapse>
				)}

				{and(Boolean(sections.get("volumeBonus")), !isDOMLoading) && (
					<Collapse sx={{ width: 1, marginTop: 2 }}>
						<SectionContainer
							sectionId={"volumeBonus"}
							title={sections.get("volumeBonus").title}
							collapsible
							deletable
							loading={loading}
							readOnly={readOnly || isReadOnly}
							onDeleteAction={handleDeleteSection}
						>
							<VolumeBonus
								sectionId={"volumeBonus"}
								position={position}
								content={data?.volumeBonus || null}
								franchises={location?.franchises || []}
								useSectionsErrors={useSectionsErrors}
								loading={loading}
								readOnly={readOnly || isReadOnly}
								onActionUpdate={handleUpdateSection}
							/>
						</SectionContainer>
					</Collapse>
				)}

				{and(Boolean(sections.get("variableOperationsGpsBonus")), !isDOMLoading) && (
					<Collapse sx={{ width: 1, marginTop: 2 }}>
						<SectionContainer
							sectionId={"variableOperationsGpsBonus"}
							title={sections.get("variableOperationsGpsBonus").title}
							collapsible
							deletable
							loading={loading}
							readOnly={readOnly || isReadOnly}
							onDeleteAction={handleDeleteSection}
						>
							<VariableOperationsGpsBonus
								sectionId={"variableOperationsGpsBonus"}
								position={position}
								content={data?.variableOperationsGpsBonus || null}
								useSectionsErrors={useSectionsErrors}
								loading={loading}
								saving={saving}
								readOnly={readOnly || isReadOnly}
								franchises={location?.franchises}
								onActionUpdate={handleUpdateSection}
							/>
						</SectionContainer>
					</Collapse>
				)}
			</TransitionGroup>

			{!isDOMLoading && (
				<Grid container item xs={12} columnSpacing={2} sx={!loading ? { paddingTop: "0px !important" } : null}>
					<TransitionGroup component={Grid} container item xs={6} alignContent={contentAlignment} alignItems={contentAlignment}>
						{sections.get("packs") && (
							<Collapse sx={{ width: 1, marginTop: 2 }}>
								<SectionContainer
									sectionId={"packs"}
									title={sections.get("packs").title}
									collapsible
									deletable
									loading={loading}
									readOnly={readOnly || isReadOnly}
									onDeleteAction={handleDeleteSection}
								>
									<Packs
										sectionId={"packs"}
										packs={data?.packs}
										errors={errors?.get("packs")}
										loading={loading}
										readOnly={readOnly || isReadOnly}
										onActionUpdate={handleUpdateSection}
									/>
								</SectionContainer>
							</Collapse>
						)}

						{sections.get("customerSatisfaction") && (
							<Collapse sx={{ width: 1, marginTop: 2 }}>
								<SectionContainer
									sectionId={"customerSatisfaction"}
									title={sections.get("customerSatisfaction").title}
									collapsible
									deletable
									loading={loading}
									readOnly={readOnly || isReadOnly}
									onDeleteAction={handleDeleteSection}
								>
									<CustomerSatisfaction
										sectionId={"customerSatisfaction"}
										content={data?.customerSatisfaction}
										errors={errors?.get("customerSatisfaction")}
										loading={loading}
										readOnly={readOnly || isReadOnly}
										onActionUpdate={handleUpdateSection}
									/>
								</SectionContainer>
							</Collapse>
						)}
					</TransitionGroup>

					<TransitionGroup component={Grid} container item xs={6} alignContent={contentAlignment} alignItems={contentAlignment}>
						{sections.get("demoAllowance") && (
							<Collapse sx={{ width: 1, marginTop: 2 }}>
								<SectionContainer
									sectionId={"demoAllowance"}
									title={sections.get("demoAllowance").title}
									collapsible
									deletable
									loading={loading}
									readOnly={readOnly || isReadOnly}
									onDeleteAction={handleDeleteSection}
								>
									<BonusAdjustments
										sectionId={"demoAllowance"}
										content={data?.demoAllowance}
										upperColumns={{
											_id: "commissions",
											left: { _id: "units", title: "Units", useValues: ["from", "to"] },
											right: { _id: "allowance", title: "Allowance", useValues: ["allowance"] },
										}}
										lowerColumns={{
											_id: "adjustments",
											actions: [{ label: "Subject to social media reviews", on: "subjectToReputation" }],
											left: { _id: "reviews", title: "Positive Reviews", useValues: ["from", "to"] },
											right: { _id: "adjustment", title: "Adjustment", useValues: ["adjustment"] },
										}}
										useSectionsErrors={useSectionsErrors}
										loading={loading}
										readOnly={readOnly || isReadOnly}
										onActionUpdate={handleUpdateSection}
									/>
								</SectionContainer>
							</Collapse>
						)}

						{sections.get("topSalesBonus") && (
							<Collapse sx={{ width: 1, marginTop: 2 }}>
								<SectionContainer
									sectionId={"topSalesBonus"}
									title={sections.get("topSalesBonus").title}
									collapsible
									deletable
									loading={loading}
									readOnly={readOnly || isReadOnly}
									onDeleteAction={handleDeleteSection}
								>
									<TopSalesBonus
										content={data?.topSalesBonus}
										useSectionsErrors={useSectionsErrors}
										loading={loading}
										readOnly={readOnly || isReadOnly}
										onActionUpdate={handleUpdateSection}
									/>
								</SectionContainer>
							</Collapse>
						)}

						{sections.get("autoAlerts") && (
							<Collapse sx={{ width: 1, marginTop: 2 }}>
								<SectionContainer
									sectionId={"autoAlerts"}
									title={sections.get("autoAlerts").title}
									collapsible
									deletable
									loading={loading}
									readOnly={readOnly || isReadOnly}
									onDeleteAction={handleDeleteSection}
								>
									<AutoAlerts
										content={data?.autoAlerts}
										useSectionsErrors={useSectionsErrors}
										loading={loading}
										readOnly={readOnly || isReadOnly}
										onActionUpdate={handleUpdateSection}
									/>
								</SectionContainer>
							</Collapse>
						)}

						{sections.get("longevity") && (
							<Collapse sx={{ width: 1, marginTop: 2 }}>
								<SectionContainer
									sectionId={"longevity"}
									title={sections.get("longevity").title}
									collapsible
									deletable
									loading={loading}
									readOnly={readOnly || isReadOnly}
									onDeleteAction={handleDeleteSection}
								>
									<Longevity
										content={data?.longevity}
										useSectionsErrors={useSectionsErrors}
										loading={loading}
										readOnly={readOnly || isReadOnly}
										onActionUpdate={handleUpdateSection}
									/>
								</SectionContainer>
							</Collapse>
						)}
					</TransitionGroup>
				</Grid>
			)}
		</PayPlansLayout>
	);
}
PayPlansModel.propTypes = {
	data: PropTypes.object,
	location: PropTypes.object,
	isNew: PropTypes.bool,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	updateModel: PropTypes.func,
};
